/**
 * Chart Conversion Utilities
 * Converts chart specifications to Plotly format.
 * Mirrors Numina's chart_functions.py build_plotly_chart() approach.
 */

/**
 * Calculate dynamic left margin based on y-axis label lengths.
 * Ensures long labels don't get cut off in horizontal bar charts.
 *
 * @param {Array} values - Array of label values
 * @param {boolean} isHorizontal - Whether this is a horizontal bar chart
 * @returns {number} - Calculated left margin in pixels
 */
const calculateLeftMargin = (values, isHorizontal = false) => {
  if (!values || values.length === 0) return 80;

  const maxLabelLength = Math.max(...values.map((v) => String(v || '').length));

  // For horizontal bars, y-axis shows labels, need more space
  if (isHorizontal) {
    // Roughly 6-8 pixels per character, with minimum of 100px and max of 300px
    return Math.min(300, Math.max(100, maxLabelLength * 7));
  }

  // For vertical charts, use default or slightly larger
  return 80;
};

/**
 * Convert chart spec to Plotly format.
 *
 * @param {Object} spec - Chart specification with encodings
 * @param {Array} data - Chart data array
 * @param {string} chartType - Type of chart to render
 * @param {string} title - Chart title
 * @returns {Object} - { plotlyData, plotlyLayout } for react-plotly.js
 */
export default function convertToPlotlyFormat(spec, data, chartType, title) {
  const encodings = spec?.encodings || {};

  // Extract field mappings (similar to Numina's approach)
  const xField = encodings.x || encodings.x_column || 'x';
  const yField = encodings.y || encodings.y_column || 'y';
  const colorField = encodings.color || encodings.color_column;
  const valueField = encodings.value || encodings.value_column || encodings.v;
  const labelField = encodings.label || encodings.label_column;

  // Normalize chart type
  const normalizedType = (chartType || '').toLowerCase().replace(/_/g, '');

  let plotlyData = [];
  const plotlyLayout = {
    title,
    autosize: true,
    margin: { l: 80, r: 50, b: 80, t: 50, pad: 4 },
    showlegend: true,
  };

  // Handle different chart types (mirroring Numina's chart_functions.py)
  switch (normalizedType) {
    case 'bar':
      plotlyData = [
        {
          type: 'bar',
          x: data.map((row) => row[xField]),
          y: data.map((row) => row[yField]),
          marker: { color: '#1890ff' },
        },
      ];
      plotlyLayout.xaxis = {
        title: xField,
        automargin: true,
      };
      plotlyLayout.yaxis = { title: yField };
      break;

    case 'horizontalbar': {
      const yAxisLabels = data.map((row) => row[yField]);
      plotlyLayout.margin.l = calculateLeftMargin(yAxisLabels, true);

      plotlyData = [
        {
          type: 'bar',
          orientation: 'h',
          x: data.map((row) => row[xField]),
          y: yAxisLabels,
          marker: { color: '#1890ff' },
        },
      ];
      plotlyLayout.xaxis = { title: xField };
      plotlyLayout.yaxis = {
        title: yField,
        automargin: true, // Automatically adjust margin for labels
      };
      break;
    }

    case 'groupedbar':
    case 'stackedbar':
      // Group data by color field
      if (colorField) {
        const groups = {};
        data.forEach((row) => {
          const group = row[colorField] || 'default';
          if (!groups[group]) {
            groups[group] = { x: [], y: [] };
          }
          groups[group].x.push(row[xField]);
          groups[group].y.push(row[yField]);
        });

        plotlyData = Object.entries(groups).map(([name, vals]) => ({
          type: 'bar',
          name,
          x: vals.x,
          y: vals.y,
        }));

        plotlyLayout.barmode = normalizedType === 'stackedbar' ? 'stack' : 'group';
        plotlyLayout.xaxis = {
          title: xField,
          automargin: true,
        };
        plotlyLayout.yaxis = { title: yField };
      } else {
        // Fallback to simple bar
        plotlyData = [
          {
            type: 'bar',
            x: data.map((row) => row[xField]),
            y: data.map((row) => row[yField]),
          },
        ];
        plotlyLayout.xaxis = {
          title: xField,
          automargin: true,
        };
        plotlyLayout.yaxis = { title: yField };
      }
      break;

    case 'line':
      plotlyData = [
        {
          type: 'scatter',
          mode: 'lines+markers',
          x: data.map((row) => row[xField]),
          y: data.map((row) => row[yField]),
          line: { color: '#1890ff' },
        },
      ];
      plotlyLayout.xaxis = {
        title: xField,
        automargin: true,
      };
      plotlyLayout.yaxis = { title: yField };
      break;

    case 'pie':
    case 'donut':
      plotlyData = [
        {
          type: 'pie',
          labels: data.map((row) => row[labelField || xField]),
          values: data.map((row) => row[valueField || yField]),
          hole: normalizedType === 'donut' ? 0.4 : 0,
          textposition: 'auto',
        },
      ];
      // Remove axis labels for pie charts
      delete plotlyLayout.xaxis;
      delete plotlyLayout.yaxis;
      break;

    case 'kpi':
    case 'kpicard': {
      const kpiValue = data[0]?.[valueField || yField];
      plotlyData = [
        {
          type: 'indicator',
          mode: 'number',
          value: kpiValue,
          title: { text: labelField ? data[0]?.[labelField] : title },
          domain: { x: [0, 1], y: [0, 1] },
        },
      ];
      plotlyLayout.height = 250;
      plotlyLayout.margin = { t: 25, b: 25, l: 25, r: 25 };
      break;
    }

    case 'scatter':
      plotlyData = [
        {
          type: 'scatter',
          mode: 'markers',
          x: data.map((row) => row[xField]),
          y: data.map((row) => row[yField]),
          marker: {
            size: 8,
            color: '#1890ff',
          },
        },
      ];
      plotlyLayout.xaxis = {
        title: xField,
        automargin: true,
      };
      plotlyLayout.yaxis = { title: yField };
      break;

    case 'table': {
      // Plotly table type
      const columnNames = Object.keys(data[0] || {});
      const columnValues = columnNames.map((col) => data.map((row) => row[col]));

      plotlyData = [
        {
          type: 'table',
          header: {
            values: columnNames,
            align: 'left',
            fill: { color: '#f0f0f0' },
            font: { size: 12, color: 'black' },
          },
          cells: {
            values: columnValues,
            align: 'left',
            fill: { color: 'white' },
            font: { size: 11, color: 'black' },
          },
        },
      ];
      break;
    }

    default:
      // Fallback to bar chart (unknown chart type)
      plotlyData = [
        {
          type: 'bar',
          x: data.map((row) => row[xField]),
          y: data.map((row) => row[yField]),
          marker: { color: '#1890ff' },
        },
      ];
      plotlyLayout.xaxis = { title: xField };
      plotlyLayout.yaxis = { title: yField };
  }

  return { plotlyData, plotlyLayout };
}
